/* IdChipThemeContext — Iter211 (extended Iter212)
 *
 * Singleton-style fetch of the ID chip style theme. Cached at the app
 * root so every IdChip render doesn't refire the request. Exposes
 * `theme` and a `refresh()` so the admin page can push updates and the
 * whole tree re-renders without a full page reload.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const makeFallback = (bg, border, text) => ({
  bg,
  border,
  text,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontWeight: '700',
  fontStyle: 'normal',
  fontSize: '0.75rem',
  letterSpacing: 'normal',
  textTransform: 'none',
  borderRadius: '0.25rem',
  borderWidth: '1px',
  borderStyle: 'solid',
  boxShadow: 'none',
  textShadow: 'none',
  paddingX: '0.375rem',
  paddingY: '0.0625rem',
});

export const ID_THEME_FALLBACK = {
  client:    makeFallback('#ECFEFF', '#A5F3FC', '#0E7490'),
  property:  makeFallback('#ECFDF5', '#A7F3D0', '#047857'),
  appraisal: makeFallback('#FEF3C7', '#FDE68A', '#92400E'),
  lead:      makeFallback('#FAF5FF', '#E9D5FF', '#6B21A8'),
};

const mergePerType = (incoming, fallback) => ({ ...fallback, ...(incoming || {}) });

const mergeTheme = (data) => ({
  client:    mergePerType(data?.client,    ID_THEME_FALLBACK.client),
  property:  mergePerType(data?.property,  ID_THEME_FALLBACK.property),
  appraisal: mergePerType(data?.appraisal, ID_THEME_FALLBACK.appraisal),
  lead:      mergePerType(data?.lead,      ID_THEME_FALLBACK.lead),
});

const IdChipThemeContext = createContext({ theme: ID_THEME_FALLBACK, refresh: () => {} });

export function IdChipThemeProvider({ children }) {
  const { api } = useAuth();
  const [theme, setTheme] = useState(ID_THEME_FALLBACK);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    api.get('/id-chip-theme')
      .then(({ data }) => {
        if (cancelled) return;
        if (data && typeof data === 'object') setTheme(mergeTheme(data));
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, [api, refreshTick]);

  const refresh = () => setRefreshTick(t => t + 1);

  return (
    <IdChipThemeContext.Provider value={{ theme, refresh }}>
      {children}
    </IdChipThemeContext.Provider>
  );
}

export function useIdChipTheme() {
  return useContext(IdChipThemeContext);
}
